import { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { API_BASE } from '../../utils/helpers';

const emptyRule = { min_amount: '', max_amount: '', percentage: '', points_valid_days: '', expiry_date: '' };

export default function PointsRules() {
  const [rules, setRules] = useState([]);
  const [formData, setFormData] = useState(emptyRule);
  const [editingId, setEditingId] = useState(null);

  const [tiers, setTiers] = useState([]);
  const [tierForm, setTierForm] = useState({ points_cost: '', discount_value: '' });
  const [tierEditing, setTierEditing] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchRules = async () => {
    try { const res = await axios.get(`${API_BASE}/api/points-rules`); setRules(res.data || []); }
    catch (err) { console.error('Failed to fetch rules', err); }
  };
  const fetchTiers = async () => {
    try { const res = await axios.get(`${API_BASE}/api/wallet/loyalty-tiers`, { headers: authHeaders() }); setTiers(res.data?.tiers || []); }
    catch (err) { console.error('Failed to fetch loyalty tiers', err); }
  };

  useEffect(() => { fetchRules(); fetchTiers(); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    const { min_amount, max_amount, percentage, points_valid_days, expiry_date } = formData;
    if (!min_amount || !max_amount || !percentage || !points_valid_days || !expiry_date) { alert('Please fill all fields.'); return; }
    if (Number(min_amount) >= Number(max_amount)) { alert('Min amount must be less than Max amount.'); return; }
    const isDuplicate = rules.some((r) => Number(r.min_amount) === Number(min_amount) && Number(r.max_amount) === Number(max_amount) && r.id !== editingId);
    if (isDuplicate) { alert('A rule with this Min/Max range already exists.'); return; }
    try {
      if (editingId) await axios.put(`${API_BASE}/api/points-rules/${editingId}`, formData);
      else await axios.post(`${API_BASE}/api/points-rules`, formData);
      setFormData(emptyRule); setEditingId(null); fetchRules();
    } catch (err) { console.error('Submit failed:', err); }
  };

  const handleEdit = (rule) => {
    setFormData({
      min_amount: rule.min_amount, max_amount: rule.max_amount, percentage: rule.percentage,
      points_valid_days: rule.points_valid_days, expiry_date: moment(rule.expiry_date).format('YYYY-MM-DD'),
    });
    setEditingId(rule.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await axios.delete(`${API_BASE}/api/points-rules/${id}`); fetchRules(); }
    catch (err) { console.error('Delete failed:', err); }
  };

  const resetTierForm = () => { setTierForm({ points_cost: '', discount_value: '' }); setTierEditing(null); };
  const submitTier = async () => {
    const points_cost = Number(tierForm.points_cost);
    const discount_value = Number(tierForm.discount_value);
    if (!points_cost || !discount_value) { alert('Enter both Points Cost and Discount Value.'); return; }
    try {
      if (tierEditing) {
        await axios.put(`${API_BASE}/api/admin/loyalty-tier`, { old_points_cost: Number(tierEditing.old_points_cost), new_points_cost: points_cost, discount_value }, { headers: authHeaders() });
      } else {
        await axios.post(`${API_BASE}/api/admin/loyalty-tier`, { points_cost, discount_value }, { headers: authHeaders() });
      }
      resetTierForm(); fetchTiers();
    } catch (err) { console.error('Submit tier failed:', err); alert('Failed to save tier'); }
  };
  const editTier = (t) => { setTierForm({ points_cost: t.points_cost, discount_value: Number(t.discount_value) }); setTierEditing({ id: t.id, old_points_cost: t.points_cost }); };
  const deleteTier = async (id) => {
    if (!window.confirm('Delete this loyalty tier?')) return;
    try { await axios.delete(`${API_BASE}/api/admin/loyalty-tier/${id}`, { headers: authHeaders() }); fetchTiers(); }
    catch (err) { console.error('Delete tier failed:', err); alert('Failed to delete tier'); }
  };

  const ruleFields = [
    { label: 'Min Amount', name: 'min_amount' }, { label: 'Max Amount', name: 'max_amount' },
    { label: 'Offer %', name: 'percentage' }, { label: 'Valid Days', name: 'points_valid_days' },
    { label: 'Expiry Date', name: 'expiry_date', type: 'date' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Points Rules</h2>
        <p className="text-sm text-slate-500">Earning rules and loyalty redemption tiers.</p>
      </div>

      {/* Rule form */}
      <section>
        <div className="ui-card p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-900">{editingId ? 'Edit rule' : 'Create rule'}</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {ruleFields.map(({ label, name, type = 'number' }) => (
              <div key={name}>
                <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
                <input className="ui-input" type={type} name={name} value={formData[name]} onChange={handleChange} />
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button className="ui-btn-primary" onClick={handleSubmit}>{editingId ? 'Update' : 'Save'}</button>
            {editingId && <button className="ui-btn-ghost" onClick={() => { setFormData(emptyRule); setEditingId(null); }}>Cancel</button>}
          </div>
        </div>
      </section>

      {/* Rules table */}
      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr><th>Min</th><th>Max</th><th>Offer %</th><th>Expiry</th><th>Valid Days</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center text-slate-400">No rules yet.</td></tr>
              ) : rules.map((rule) => (
                <tr key={rule.id}>
                  <td className="font-medium text-slate-900">{rule.min_amount}</td>
                  <td className="font-medium text-slate-900">{rule.max_amount}</td>
                  <td>{rule.percentage}%</td>
                  <td className="whitespace-nowrap text-slate-500">{moment(rule.expiry_date).format('YYYY-MM-DD')}</td>
                  <td className="text-slate-500">{rule.points_valid_days}</td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => handleEdit(rule)}><FaEdit className="text-xs" /> Edit</button>
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50" onClick={() => handleDelete(rule.id)}><FaTrash className="text-xs" /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Loyalty tiers */}
      <section>
        <div className="ui-card p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-900">{tierEditing ? 'Edit loyalty tier' : 'Create loyalty tier'}</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Points Cost</label>
              <input className="ui-input" type="number" value={tierForm.points_cost} onChange={(e) => setTierForm({ ...tierForm, points_cost: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Discount Value</label>
              <input className="ui-input" type="number" value={tierForm.discount_value} onChange={(e) => setTierForm({ ...tierForm, discount_value: e.target.value })} />
            </div>
            <div className="flex items-end gap-2">
              <button className="ui-btn-primary" onClick={submitTier}>{tierEditing ? 'Update' : 'Save'}</button>
              {tierEditing && <button className="ui-btn-ghost" onClick={resetTierForm}>Cancel</button>}
            </div>
          </div>
        </div>
      </section>

      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr><th className="w-12">#</th><th>Points Cost</th><th>Discount Value</th><th>Shopify Price Rule ID</th><th>Active</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {tiers.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center text-slate-400">No loyalty tiers yet.</td></tr>
              ) : tiers.map((t, idx) => (
                <tr key={t.id}>
                  <td className="text-slate-400">{idx + 1}</td>
                  <td className="font-medium text-slate-900">{t.points_cost}</td>
                  <td>{Number(t.discount_value).toFixed(2)}</td>
                  <td className="font-mono text-xs text-slate-500">{t.shopify_price_rule_id || '—'}</td>
                  <td>
                    <span className={`ui-badge ${String(t.is_active) === '1' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {String(t.is_active) === '1' ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => editTier(t)}><FaEdit className="text-xs" /> Edit</button>
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50" onClick={() => deleteTier(t.id)}><FaTrash className="text-xs" /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
